/**
 * §P.1 fase 2 · La tarjeta de fichaje de un alumno.
 *
 * ## El token se enseña **una sola vez**, y de eso responde esta pantalla
 *
 * En `student_cards` solo queda el hash, así que quien se lleve una copia de la tabla
 * no se lleva ninguna tarjeta que funcione. La contrapartida es que **no hay forma de
 * volver a enseñar el valor**: si se pierde, se emite otra.
 *
 * El contrato es explícito sobre de quién es el problema: «eso no es un olvido de la
 * interfaz, es la garantía: **decidlo en la pantalla antes** de que alguien cierre el
 * diálogo sin imprimir». Así que aquí:
 *
 * - Se avisa **antes** de emitir, no después.
 * - El token aparece en una caja que no se puede cerrar por descuido: hay que pulsar
 *   «Ya lo he guardado», y ese botón dice lo que significa.
 * - Se ofrece copiarlo, porque el caso real es pegarlo en el programa que graba el
 *   llavero NFC o en el que imprime el carnet.
 *
 * ## Emitir una segunda revoca la anterior
 *
 * Es lo que pasa de verdad —nadie quiere dos llaveros vivos del mismo niño— y el
 * índice único lo impide igualmente. Se dice antes de emitir, que es cuando importa.
 *
 * ## Revocar no borra
 *
 * El fichaje del martes pasado tiene que seguir explicándose, y una tarjeta borrada
 * dejaría entradas de registro sin nada detrás.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha, fechaYHora } from '@/shared/ui/formato';
import {
  CLASES_DE_TARJETA,
  etiquetaDeClaseDeTarjeta,
  type ClaseDeTarjeta,
  type TarjetaDeAlumno,
  type TarjetaEmitida,
} from '@/club/alumnos/administracion/tipos';

export function TarjetaDeFichaje({
  club,
  alumnoId,
  puedeEditar,
}: {
  club: string;
  alumnoId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [emitiendo, setEmitiendo] = useState(false);
  const [clase, setClase] = useState<ClaseDeTarjeta>('qr');
  const [etiqueta, setEtiqueta] = useState('');
  /** El token recién emitido. **No se vuelve a pedir nunca.** */
  const [reciente, setReciente] = useState<TarjetaEmitida | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [conRevocadas, setConRevocadas] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const tarjetas = useQuery({
    queryKey: ['tarjetas', club, alumnoId, conRevocadas],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .cards.get({ query: conRevocadas ? { revoked: true } : {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TarjetaDeAlumno[];
    },
  });

  const invalidar = () =>
    void cache.invalidateQueries({ queryKey: ['tarjetas', club, alumnoId] });

  const emitir = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .cards.post({
          kind: clase,
          label: etiqueta.trim() === '' ? null : etiqueta.trim(),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as TarjetaEmitida;
    },
    onSuccess: (t) => {
      setEmitiendo(false);
      setEtiqueta('');
      setCopiado(false);
      setReciente(t);
      invalidar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const revocar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .cards({ cardId: id })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: invalidar,
    onError: (e) => setProblema(aProblema(e)),
  });

  // ── El token, una sola vez ───────────────────────────────────────────────
  if (reciente) {
    return (
      <div className="hueco hueco-confirmar">
        <h3>Esta es la tarjeta de fichaje</h3>

        <div className="aviso aviso-grave" role="alert">
          <strong>Guárdala ahora: no se puede volver a ver.</strong> Kiap solo
          conserva una huella suya, no el código. Si lo pierdes, la única salida
          es emitir otra tarjeta.
        </div>

        {/* El valor, en monoespaciado y seleccionable de un tirón. */}
        <p className="token">
          <code>{reciente.token}</code>
        </p>

        <div className="hueco-acciones">
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(reciente.token)
                .then(() => setCopiado(true))
                /*
                 * El portapapeles falla sin HTTPS y sin permiso, y no pasa nada: el
                 * token está en pantalla y se puede seleccionar a mano. Lo que no
                 * vale es que el botón se quede mudo.
                 */
                .catch(() => setCopiado(false));
            }}
          >
            {copiado ? 'Copiado' : 'Copiar el código'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => setReciente(null)}
          >
            Ya lo he guardado
          </button>
        </div>

        <p className="tenue">
          {clase === 'nfc'
            ? 'Grábalo en el llavero con el programa del lector.'
            : 'Pégalo en el generador de QR del carnet, o mándaselo a la familia para que lo guarde en el móvil.'}
        </p>
      </div>
    );
  }

  // ── Emitir ───────────────────────────────────────────────────────────────
  if (emitiendo) {
    const viva = tarjetas.data?.find((t) => t.active && t.kind === clase);
    const elegida = CLASES_DE_TARJETA.find((c) => c.valor === clase);

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          emitir.mutate();
        }}
      >
        <div className="rejilla">
          <label>
            Qué tipo
            <select
              value={clase}
              onChange={(e) => setClase(e.target.value as ClaseDeTarjeta)}
            >
              {CLASES_DE_TARJETA.map((c) => (
                <option key={c.valor} value={c.valor}>
                  {c.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label>
            Cómo llamarla
            <input
              maxLength={60}
              placeholder="Llavero de la mochila"
              value={etiqueta}
              onChange={(e) => setEtiqueta(e.target.value)}
            />
          </label>
        </div>

        {elegida && <p className="tenue">{elegida.explicacion}</p>}

        {/* Lo que va a pasar, antes de que pase. */}
        <div className="aviso" role="status">
          <p>
            <strong>El código se enseña una sola vez.</strong> Ten a mano dónde
            pegarlo antes de pulsar: después no se puede volver a ver, y la
            única salida sería emitir otra.
          </p>
          {viva && (
            /*
              **Sin declinar el tipo.** Decía «revoca la código qr que ya tiene»: «el
              código QR» es masculino y «la tarjeta NFC» femenina, y minusculizar la
              etiqueta encima destrozaba el acrónimo. Como `viva` se busca por el
              mismo tipo que está elegido justo arriba, nombrarlo aquí no añade nada:
              «la que ya tiene de este tipo» se lee bien con los dos.
            */
            <p>
              <strong>Esto revoca la que ya tiene de este tipo</strong>
              {viva.label ? ` («${viva.label}»)` : ''} y dejará de funcionar en
              el lector.
            </p>
          )}
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={emitir.isPending}>
            {emitir.isPending ? 'Emitiendo…' : 'Emitir la tarjeta'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={emitir.isPending}
            onClick={() => {
              setEmitiendo(false);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── La lista ─────────────────────────────────────────────────────────────
  if (tarjetas.isPending) {
    return <Cargando filas={2} etiqueta="Cargando las tarjetas" />;
  }

  if (tarjetas.isError || !tarjetas.data) {
    return (
      <PanelError
        problema={aProblema(tarjetas.error)}
        reintentar={() => void tarjetas.refetch()}
      />
    );
  }

  return (
    <div>
      <div className="tutor-acciones" style={{ marginBlockStart: 0 }}>
        {puedeEditar && (
          <button type="button" onClick={() => setEmitiendo(true)}>
            Emitir una tarjeta
          </button>
        )}
        <button
          type="button"
          className="btn-plano"
          onClick={() => setConRevocadas((x) => !x)}
        >
          {conRevocadas
            ? 'Solo las que funcionan'
            : 'Ver también las revocadas'}
        </button>
      </div>

      {problema && <PanelError problema={problema} />}

      {tarjetas.data.length === 0 ? (
        <p className="tenue">
          No tiene tarjeta. Sirve para fichar la entrada sin que nadie pase
          lista a mano: un QR en el carnet o un llavero que se acerca al lector.
        </p>
      ) : (
        <ul className="papeles">
          {tarjetas.data.map((t) => (
            <li key={t.id} className={t.active ? undefined : 'fila-fuera'}>
              <div className="aviso-cabeza">
                <strong>{etiquetaDeClaseDeTarjeta(t.kind)}</strong>
                {t.active ? (
                  <span className="pastilla pastilla-bien">Funciona</span>
                ) : (
                  <span className="pastilla pastilla-mal">Revocada</span>
                )}
                {t.label && (
                  <span className="pastilla pastilla-left">{t.label}</span>
                )}
              </div>

              <p className="tenue">
                Emitida el {fecha(t.issued_on)}
                {t.revoked_at ? ` · revocada ${fechaYHora(t.revoked_at)}` : ''}
              </p>

              {puedeEditar && t.active && (
                <div className="aviso-pie">
                  <span className="tenue">
                    {/* El código no se puede reenseñar: se dice donde se busca. */}
                    El código no se puede volver a ver.
                  </span>
                  <button
                    type="button"
                    className="btn-plano"
                    disabled={revocar.isPending}
                    onClick={() => revocar.mutate(t.id)}
                  >
                    Revocarla
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
